import { err, ok, Result } from "neverthrow";
import { GraphqlApi, IGraphqlApi } from "@/framework/network/gql/GraphqlApi";
import { z, ZodError } from "zod";
import {
  ChatTypingDocument,
  ChatTypingMutation,
  CheckGuestPhoneNumberResponse,
  CheckIfUserIsAvailableDocument,
  CheckIfUserIsAvailableQuery,
  CheckMessageIsGuestUnreadDocument,
  CheckMessageIsGuestUnreadQuery,
  CheckPhoneNumberDocument,
  CheckPhoneNumberQuery,
  CreateConversationDocument,
  CreateConversationMutation,
  CreateSmsConversationDocument,
  CreateSmsConversationMutation,
  CreateVoiceCallTokenDocument,
  CreateVoiceCallTokenMutation,
  DealWithMessageDocument,
  DealWithMessageMutation,
  DeleteQcUnreadConversationDocument,
  DeleteQcUnreadConversationMutation,
  DeleteQcUnreadMessageDocument,
  DeleteQcUnreadMessageMutation,
  GeneralIdResponse,
  GetConversationInTenantDocument,
  GetConversationInTenantQuery,
  GetConversationV2Query,
  GetListStaffInGroupDocument,
  GetListStaffInGroupQuery,
  GetMissingMessagesDocument,
  GetMissingMessagesQuery,
  GetRoomsDocument,
  GetRoomsQuery,
  IsConversationMemberDocument,
  IsConversationMemberQuery,
  ListConversationIdsDocument,
  ListConversationIdsQuery,
  ListConversationsV2Document,
  ListConversationsV2Query,
  ListQcUnreadMessageDocument,
  ListQcUnreadMessageQuery,
  PostMessageDocument,
  PostMessageInput,
  PostMessageMutation,
  ReadConversationDocument,
  ReadConversationMutation,
  ResponseByQuickReplyDocument,
  ResponseByQuickReplyMutation,
  ReTranslateMessageDocument,
  ReTranslateMessageMutation,
} from "@/framework/network/gql/generated/graphql";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import { removeTypename } from "@/framework/utilities/graphqlUtil";
import { ConversationState } from "@/framework/services/events/subscription";
import { QcConversation, QcConversationSchema } from "@/modules/staff/objects/conversation/QcConversation";
import { CustomGetConversationV2 } from "@/framework/network/gql/custom/GetConversationV2/CustomGetConversationV2";
import { CustomGetConversationV2Shallow } from "@/framework/network/gql/custom/GetConversationV2/CustomGetConversationV2Shallow";
import { ConvoResponse } from "@/router/guards";
import { GeneralResponse, GeneralResponseSchema } from "@/framework/objects/GeneralResponse";
import { GeneralIdResponseSchema } from "@/framework/objects/GeneralIdResponse";
import {
  SmsConversationResponse,
  SmsConversationResponseSchema,
} from "@/modules/staff/objects/conversation/SmsConversationResponse";
import {
  CheckPhoneNumberResponse,
  CheckPhoneNumberResponseSchema,
} from "@/modules/staff/objects/conversation/CheckPhoneNumberResponse";
import { CheckGuestPhoneNumberResponseSchema } from "@/modules/staff/objects/conversation/CheckGuestPhoneNumberResponse";
import { Rooms, RoomsSchema } from "@/modules/staff/objects/guest/Rooms";
import {
  PostMessageResponse,
  PostMessageResponseSchema,
} from "@/modules/staff/objects/conversation/PostMessageResponse";
import { QcUnreadMessage, QcUnreadMessageSchema } from "@/modules/staff/objects/conversation/QcUnreadMessage";
import { VoiceChatToken, VoiceChatTokenSchema } from "@/modules/staff/objects/conversation/VoiceChatToken";
import {
  ModelConversationListForConversationList,
  ModelConversationListForConversationListSchema,
} from "@/modules/staff/objects/conversation/ModelConversationListForConversationList";
import {
  CheckMessageIsGuestUnreadResponse,
  CheckMessageIsGuestUnreadResponseSchema,
} from "@/modules/staff/objects/conversation/CheckMessageIsGuestUnreadResponse";
import {
  ListStaffInGroupResponse,
  ListStaffInGroupResponseSchema,
} from "@/modules/staff/objects/group/ListStaffInGroupResponse";
import {
  ModelQcUnreadMessageList,
  ModelQcUnreadMessageListSchema,
} from "@/modules/staff/objects/conversation/ModelQcUnreadMessageList";
import { Message, MessageSchema } from "@/modules/staff/objects/conversation/Message";

export class ConversationService {
  private _graphql: IGraphqlApi;

  public constructor(graphql: IGraphqlApi = new GraphqlApi()) {
    this._graphql = graphql;
  }

  fetchMissingMessages = async (
    conversationId: string,
    orders: number[]
  ): Promise<Result<Message[], GraphqlApiError>> => {
    const result = await this._graphql.runQueryAndValidate<GetMissingMessagesQuery, Message[]>(
      GetMissingMessagesDocument,
      MessageSchema,
      { conversationId, orders }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  };

  async respondQuickReply(payload: string, conversation_id: string): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<ResponseByQuickReplyMutation, GeneralResponse>(
      ResponseByQuickReplyDocument,
      GeneralResponseSchema,
      {
        payload,
        conversation_id,
      }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async createConversation(userId: string): Promise<Result<GeneralIdResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<CreateConversationMutation, GeneralIdResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      CreateConversationDocument,
      GeneralIdResponseSchema,
      {
        userId: userId,
      }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async createSmsConversation(
    langCd: string,
    locationId: number,
    message: string,
    phoneNumber: string
  ): Promise<Result<SmsConversationResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<CreateSmsConversationMutation, SmsConversationResponse>(
      CreateSmsConversationDocument,
      SmsConversationResponseSchema,
      {
        langCd: langCd,
        locationId: locationId,
        message: message,
        phoneNumber: phoneNumber,
      }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async checkPhoneNumber(number: string): Promise<Result<CheckPhoneNumberResponse, GraphqlApiError>> {
    const result = await this._graphql.runQueryAndValidate<CheckPhoneNumberQuery, Array<CheckPhoneNumberResponse>>(
      CheckPhoneNumberDocument,
      CheckPhoneNumberResponseSchema,
      { number: number }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value[0]);
    }
  }

  // from the API comments in BE project:
  // The value of isAvailable is as follow:
  //   if phone number is empty: False
  //   if phone number has no record in the DB: True
  //   if phone number exists in DB, retrieve the associated conversation:
  //   - if the conversation state is sms_chat: False
  //   - in other cases: True
  async checkCanUsePhone(number: string): Promise<Result<CheckGuestPhoneNumberResponse, GraphqlApiError>> {
    const result = await this._graphql.runQueryAndValidate<CheckIfUserIsAvailableQuery, CheckGuestPhoneNumberResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      CheckIfUserIsAvailableDocument,
      CheckGuestPhoneNumberResponseSchema,
      { phoneNumber: number }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async getRooms(page?: number, count?: number): Promise<Result<Rooms, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetRoomsQuery, Rooms>(GetRoomsDocument, RoomsSchema, {
      page: page,
      count: count,
    });
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async sendMessage(message: PostMessageInput): Promise<Result<PostMessageResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<PostMessageMutation, PostMessageResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      PostMessageDocument,
      PostMessageResponseSchema,
      { input: message }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async deleteQcUnreadMessage(
    id: number,
    userId?: string
  ): Promise<Result<QcUnreadMessage, GraphqlApiError | ZodError>> {
    const result = await this._graphql.runMutationAndValidate<DeleteQcUnreadMessageMutation, QcUnreadMessage>(
      // @ts-ignore conflict between generic typing and exact requirement
      DeleteQcUnreadMessageDocument,
      QcUnreadMessageSchema,
      { id: id, userId: userId }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async reTranslateMessage(messageId: string): Promise<Result<GeneralResponse, GraphqlApiError>> {
    // removed  "| ZodError" until, if ever, a def for GeneralResponse is made
    const result = await this._graphql.runMutationAndValidate<ReTranslateMessageMutation, GeneralResponse>(
      ReTranslateMessageDocument,
      GeneralResponseSchema,
      { messageId: messageId }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async setChatTyping(
    conversationId: string,
    status: string
  ): Promise<Result<GeneralResponse, GraphqlApiError | ZodError>> {
    const result = await this._graphql.runMutationAndValidate<ChatTypingMutation, GeneralResponse>(
      ChatTypingDocument,
      GeneralResponseSchema,
      { conversation_id: conversationId, status: status }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async createVoiceChatToken(sessionId: string): Promise<Result<VoiceChatToken, GraphqlApiError | ZodError>> {
    const result = await this._graphql.runMutationAndValidate<CreateVoiceCallTokenMutation, VoiceChatToken>(
      // @ts-ignore conflict between generic typing and exact requirement
      CreateVoiceCallTokenDocument,
      VoiceChatTokenSchema,
      { sessionId: sessionId }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async fetchConversationIds(mode: string): Promise<Result<string[], GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<ListConversationIdsQuery, string[]>(
      ListConversationIdsDocument,
      z.string(),
      { mode }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async fetchConversationV2List(
    page: number,
    limit: number,
    mode: string,
    bizConvEnabled: boolean = true
  ): Promise<Result<ModelConversationListForConversationList, GraphqlApiError>> {
    const v = { page, limit, mode, bizConvEnabled };
    const res = await this._graphql.runQueryAndValidate<
      ListConversationsV2Query,
      ModelConversationListForConversationList
    >(ListConversationsV2Document, ModelConversationListForConversationListSchema, v);

    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async isMessageGuestUnread(messageId: string): Promise<Result<CheckMessageIsGuestUnreadResponse, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<
      CheckMessageIsGuestUnreadQuery,
      CheckMessageIsGuestUnreadResponse
    >(
      // @ts-ignore conflict between generic typing and exact requirement
      CheckMessageIsGuestUnreadDocument,
      CheckMessageIsGuestUnreadResponseSchema,
      { messageId }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async getStaffInGroup(groupId: number): Promise<Result<ListStaffInGroupResponse[], GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetListStaffInGroupQuery, ListStaffInGroupResponse[]>(
      // @ts-ignore conflict between generic typing and exact requirement
      GetListStaffInGroupDocument,
      ListStaffInGroupResponseSchema,
      { groupId: groupId }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async handleMessage(messageId: string, action: string): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<DealWithMessageMutation, GeneralResponse>(
      DealWithMessageDocument,
      GeneralResponseSchema,
      {
        messageId: messageId,
        action: action,
      }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async deleteUnreadConversation(conversationId: string): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<DeleteQcUnreadConversationMutation, GeneralResponse>(
      DeleteQcUnreadConversationDocument,
      GeneralResponseSchema,
      { conversationId: conversationId }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async readConversationStatus(
    conversationId: string
  ): Promise<Result<ModelConversationListForConversationList, GraphqlApiError>> {
    const v = { conversationId };
    const res = await this._graphql.runMutationAndValidate<
      ReadConversationMutation,
      ModelConversationListForConversationList
    >(
      // @ts-ignore conflict between generic typing and exact requirement
      ReadConversationDocument,
      ModelConversationListForConversationListSchema,
      v
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async isConvMember(conversationState: ConversationState): Promise<Result<boolean, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<IsConversationMemberQuery, boolean>(
      IsConversationMemberDocument,
      z.boolean(),
      { conversationId: conversationState.conversation_id }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async listUnreadMessage(limit = 0, page = 1): Promise<Result<ModelQcUnreadMessageList, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<ListQcUnreadMessageQuery, ModelQcUnreadMessageList>(
      ListQcUnreadMessageDocument,
      ModelQcUnreadMessageListSchema,
      { page, limit }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async getConversation(
    conversationId: string,
    messageLimit = 20,
    nextToken: string | null = null,
    isAdmin = false
  ): Promise<Result<QcConversation, GraphqlApiError>> {
    if (isAdmin) {
      const variables = {
        id: conversationId,
        messageLimit: messageLimit,
        messageNextToken: nextToken,
        isAdmin: isAdmin,
      };
      const res = await this._graphql.runQueryAndValidate<GetConversationInTenantQuery, QcConversation>(
        GetConversationInTenantDocument,
        QcConversationSchema,
        variables,
        true
      );
      return res.isErr() ? err(res.error) : ok(res.value);
    } else {
      const variables = {
        id: conversationId,
        messageLimit: messageLimit,
        messageNextToken: nextToken,
      };
      const res = await this._graphql.runQuery(CustomGetConversationV2, variables, true);
      return res.isErr() ? err(res.error) : removeTypename(res.value.getConversationV2);
    }
  }

  async getConversationShallow(v: {}): Promise<Result<ConvoResponse, GraphqlApiError>> {
    const res = await this._graphql.runQuery(CustomGetConversationV2Shallow, v, true);
    return res.isErr() ? err(res.error) : removeTypename(res.value.getConversationV2);
  }

  isEncodedUrl(url: string): boolean {
    try {
      return decodeURIComponent(url) !== url;
    } catch (e) {
      return false;
    }
  }

  // Matches any string, immediately followed by a link without space
  // e.g. "This is a linkhttps://google.com"
  urlRegex = /^(.*?)(https?:\/\/\S+)$/i;

  containsValidUrl = (text: string): boolean => {
    return this.urlRegex.test(text);
  };

  /**
   * Returns the text part of a message chuck that includes a link
   * @example
   * getTextPart("linkhttps://google.com") // "link";
   */
  getTextPart = (text: string): string => {
    const match = text.match(this.urlRegex);
    return match ? match[1] : "";
  };

  /**
   * Returns the Url part of a message chunk that includes a link, encoding where necessary.
   * @example
   * getUrlPart("linkhttps://google.com") // "https://google.com";
   * getUrlPart("linkhttps://ja.wikipedia.org/wiki/カピバラ" //"https://ja.wikipedia.org/wiki/%E3%82%AB%E3%83%94%E3%83%90%E3%83%A9"
   */
  getUrlPart = (text: string): string => {
    const match = text.match(this.urlRegex);
    const rawUrl: string = match ? match[2] : "";
    return this.isEncodedUrl(rawUrl) ? rawUrl : encodeURI(rawUrl);
  };

  /**
   * Encodes any urls in the message that the staff sends in UTF-8 format.
   * @param {string} input - the message that the staff sends.
   * @return {string} - the same message, with urls properly encoded in UTF-8 format.
   *
   * @example
   * const input = "Please see this url: https://ja.wikipedia.org/wiki/カピバラ it is cool";
   * console.log(encodeUrlInMessage(input)); // "Please see this url: https://ja.wikipedia.org/wiki/%E3%82%AB%E3%83%94%E3%83%90%E3%83%A9 it is cool"
   */
  encodeUrlInMessage = (input: string): string => {
    // split message by whitespace
    const inputSplitBySpace: string[] = input.split(/\s+/);
    inputSplitBySpace.forEach((item, i) => {
      if (this.containsValidUrl(item)) {
        inputSplitBySpace[i] = this.getTextPart(item) + this.getUrlPart(item);
      }
    });
    return inputSplitBySpace.join(" ");
  };
}
